import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "src/dm/utils/axios";
import { APIS } from "src/dm/utils/constants";
import { DealDetail } from "src/dm/interfaces/Deal";

export const useCopyDeal = (dealId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<DealDetail> => {
      const response = await apiClient.post<DealDetail>(
        `${APIS.Deals}/${dealId}/copy`
      );
      return response.data;
    },
    onSuccess: (deal) => {
      qc.invalidateQueries({ queryKey: ["deals"] });
      qc.setQueryData(["deal", deal.dealId], deal);
    },
  });
};

export interface StatusChangeResult {
  deal: DealDetail;
  validatedAgainst: string;
  errorCount: number;
  plsqlError: string | null;
}

export interface StatusChangeBlocked {
  blocked: true;
  targetStatus: string;
  validatedAgainst: string;
  errorCount: number;
  plsqlError: string | null;
  message: string;
}

export const useChangeStatus = (dealId: number) => {
  const qc = useQueryClient();
  return useMutation<StatusChangeResult, unknown, { status: string; force?: boolean }>({
    mutationFn: async ({ status, force }) => {
      const url = `${APIS.Deals}/${dealId}/status${force ? "?force=true" : ""}`;
      const response = await apiClient.post<StatusChangeResult>(url, { status });
      return response.data;
    },
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ["deals"] });
      qc.setQueryData(["deal", result.deal.dealId], result.deal);
      qc.invalidateQueries({ queryKey: ["deal-history", result.deal.dealId] });
      qc.invalidateQueries({ queryKey: ["deal-signatures", result.deal.dealId] });
      qc.invalidateQueries({ queryKey: ["deal-errors", result.deal.dealId] });
    },
  });
};

interface ValidateResponse {
  dealId: number;
  targetStatus: string;
  validated: boolean;
  errorCount: number;
  noteCount: number;
  plsqlError?: string | null;
}

export const useValidateDeal = (dealId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<ValidateResponse> => {
      const response = await apiClient.post<ValidateResponse>(
        `${APIS.Deals}/${dealId}/validate`
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deal-errors", dealId] });
      qc.invalidateQueries({ queryKey: ["deal", dealId] });
    },
  });
};

export const useSetLock = (dealId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (locked: boolean): Promise<DealDetail> => {
      const response = await apiClient.post<DealDetail>(
        `${APIS.Deals}/${dealId}/lock`,
        { locked }
      );
      return response.data;
    },
    onSuccess: (deal) => {
      qc.setQueryData(["deal", dealId], deal);
      qc.invalidateQueries({ queryKey: ["deals"] });
    },
  });
};

export type ReportType = "detail" | "summary" | "overview";

export const openPrintReport = async (dealId: number, reportType: ReportType = "detail") => {
  const response = await apiClient.get<{ url: string }>(
    `${APIS.Deals}/${dealId}/print-url`,
    { params: { reportType } }
  );
  window.open(response.data.url, "_blank", "noopener,noreferrer");
};

export const useSetTermSheet = (dealId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (value: boolean): Promise<DealDetail> => {
      const response = await apiClient.post<DealDetail>(
        `${APIS.Deals}/${dealId}/term-sheet`,
        { value }
      );
      return response.data;
    },
    onSuccess: (deal) => {
      qc.setQueryData(["deal", dealId], deal);
    },
  });
};

interface BatchValidateResponse {
  dealId: number;
  queued: boolean;
  targetStatus: string;
  errorCount: number;
  plsqlError: string | null;
  note: string;
}

export const useBatchValidate = (dealId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<BatchValidateResponse> => {
      const response = await apiClient.post<BatchValidateResponse>(
        `${APIS.Deals}/${dealId}/batch-validate`
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deal-errors", dealId] });
      qc.invalidateQueries({ queryKey: ["deal", dealId] });
    },
  });
};

/**
 * EndDeal — POST /api/dealmanagement/deals/{dealId}/end. Sends a new
 * promo end date (and optional human reason) to the server, which runs the
 * cascade trim (PROMO_END → ScanbackEnd → ReflectEnd → CountRecountEnd →
 * LastOrder → LastShipment → RewardPromoEnd), enforces the ad-week-window
 * guard, persists, then fans out the "Deal Ended Early" auditor
 * notification. See audit 14-parity-matrix.md §5 item 8.
 */
export interface EndDealRequest {
  promoEndDate: string; // ISO date, e.g. "2026-05-30"
  reason?: string;
}

export const useEndDeal = (dealId: number) => {
  const qc = useQueryClient();
  return useMutation<DealDetail, unknown, EndDealRequest>({
    mutationFn: async (body) => {
      const response = await apiClient.post<DealDetail>(
        `${APIS.Deals}/${dealId}/end`,
        body
      );
      return response.data;
    },
    onSuccess: (deal) => {
      qc.setQueryData(["deal", dealId], deal);
      qc.invalidateQueries({ queryKey: ["deals"] });
      qc.invalidateQueries({ queryKey: ["deal-history", dealId] });
    },
  });
};

/**
 * CopyDeal with full DealCopyRequest body — extends the existing
 * {@link useCopyDeal} with form-override + copyAttachments support. Mirrors
 * the legacy CopyDeal.aspx 25-field form including the chkCopyAttachments
 * toggle. See audit 14-parity-matrix.md §5 item 10.
 */
export interface DealCopyOverrides {
  status?: "NEW" | "PREPARED";
  description?: string;
  bannerCode?: string;
  categoryManagerCode?: number;
  dealTypeId?: number;
  adStartDate?: string;
  adEndDate?: string;
  promoStartDate?: string;
  promoEndDate?: string;
  scanbackStartDate?: string;
  scanbackEndDate?: string;
  reflectStartDate?: string;
  reflectEndDate?: string;
  countRecountStartDate?: string;
  countRecountEndDate?: string;
  firstOrderDate?: string;
  lastOrderDate?: string;
  firstShipmentDate?: string;
  lastShipmentDate?: string;
  bestHistoryDate?: string;
  signageEndDate?: string;
  rewardPromoEndDate?: string;
  promoMaxQty?: number;
  hasOffInvoiceAllowance?: boolean;
  hasBillBackAllowance?: boolean;
  hasCountRecountAllowance?: boolean;
  hasPassThruAllowance?: boolean;
  hasScanbackAllowance?: boolean;
  isRewardPromotion?: boolean;
  // Owner override — legacy CopyDeal.aspx exposed ddlDealOwner inline so the
  // copy could be re-assigned at the same time. Server already accepts this
  // on DealCopyRequest.OwnerUserId (see DealCopyRequest.cs:63).
  ownerUserId?: string;
  copyAttachments?: boolean;
  // (Any other DealWriteRequest field can be added here when the SPA needs
  // it — the server treats every field as optional on the copy body.)
}

export const useCopyDealWithOverrides = (dealId: number) => {
  const qc = useQueryClient();
  return useMutation<DealDetail, unknown, DealCopyOverrides>({
    mutationFn: async (overrides) => {
      const response = await apiClient.post<DealDetail>(
        `${APIS.Deals}/${dealId}/copy`,
        overrides
      );
      return response.data;
    },
    onSuccess: (deal) => {
      qc.invalidateQueries({ queryKey: ["deals"] });
      qc.setQueryData(["deal", deal.dealId], deal);
    },
  });
};

export const useReturnToVendor = (dealId: number) => {
  const qc = useQueryClient();
  return useMutation<StatusChangeResult, unknown, { force?: boolean }>({
    mutationFn: async ({ force }) => {
      const url = `${APIS.Deals}/${dealId}/return-to-vendor${force ? "?force=true" : ""}`;
      const response = await apiClient.post<StatusChangeResult>(url);
      return response.data;
    },
    onSuccess: (result) => {
      qc.setQueryData(["deal", dealId], result.deal);
      qc.invalidateQueries({ queryKey: ["deals"] });
      qc.invalidateQueries({ queryKey: ["deal-history", dealId] });
    },
  });
};
